import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import AddressInput from "@/components/AddressInput";
import { Plus } from "lucide-react";
import { toast } from "sonner";

const RELATIONSHIPS = [
    "Partner",
    "Spouse",
    "Child",
    "Parent",
    "Sibling",
    "Other adult",
];
const GENDERS = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "nonbinary", label: "Non-binary" },
    { value: "prefer_not_to_say", label: "Prefer not to say" },
];
const REGIONS = [
    { value: "HK", label: "Hong Kong SAR" },
    { value: "MO", label: "Macao SAR" },
    { value: "SG", label: "Singapore" },
    { value: "TW", label: "Taiwan" },
    { value: "CN", label: "Mainland China" },
    { value: "OTHER", label: "Other" },
];

const INITIAL_FORM = {
    name: "",
    relationship: "Partner",
    dob: "",
    email: "",
    phone: "",
    gender: "",
    region: "HK",
    addr: { address_line: "" },
    password: "",
};

/**
 * Shared add/edit dialog for secondary members (adults and dependants).
 * mode="create" with no preset -> plain "Add secondary member".
 * mode="create" with presetProviderId/-Name -> "Add dependant" flow,
 *   triggered from a specific adult's row; primary_provider_id is sent
 *   automatically, never exposed as an editable field.
 * mode="edit" -> unchanged from the original inline dialog; provider
 *   reassignment isn't exposed here (backend supports it via PATCH if
 *   ever needed later).
 */
export default function SecondaryUserFormDialog({
    open,
    onOpenChange,
    mode,
    editingRow,
    presetProviderId,
    presetProviderName,
    onSaved,
}) {
    const [form, setForm] = useState(INITIAL_FORM);
    const [saving, setSaving] = useState(false);
    const editing = mode === "edit" ? editingRow : null;

    useEffect(() => {
        if (!open) return;
        if (editing) {
            setForm({
                name: editing.name || "",
                relationship: editing.relationship || "Partner",
                dob: editing.dob || "",
                email: editing.email || "",
                phone: editing.phone || "",
                gender: editing.gender || "",
                region: editing.region || "HK",
                addr: {
                    address_line: editing.address_line || "",
                    address_place_id: editing.address_place_id,
                    address_lat: editing.address_lat,
                    address_lng: editing.address_lng,
                    address_components: editing.address_components,
                },
                password: "",
            });
        } else {
            setForm(INITIAL_FORM);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editing?.id]);

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                relationship: form.relationship,
                dob: form.dob,
                email: form.email?.trim() || null,
                phone: form.phone || null,
                gender: form.gender || null,
                region: form.region,
                address_line: form.addr.address_line || null,
                address_place_id: form.addr.address_place_id || null,
                address_lat: form.addr.address_lat || null,
                address_lng: form.addr.address_lng || null,
                address_components: form.addr.address_components || null,
            };
            if (editing) {
                await api.patch(`/member/sub-users/${editing.id}`, payload);
                toast.success(`${payload.name} updated`);
            } else {
                if (form.password) payload.password = form.password;
                if (presetProviderId) payload.primary_provider_id = presetProviderId;
                await api.post("/member/sub-users", payload);
                toast.success(`${payload.name} added to your family`);
            }
            onSaved?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    }

    const title = editing
        ? `Edit ${editing.name}`
        : presetProviderName
          ? `Add a dependant of ${presetProviderName}`
          : "Add a secondary member";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="secondary-dialog"
                className="bg-white border-navy-100 max-w-2xl max-h-[92vh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display text-2xl text-navy-700">
                        {title}
                    </DialogTitle>
                    <DialogDescription className="text-navy-500">
                        All the same details we capture for a primary member. Age
                        is derived from date of birth — under-18s become free
                        dependants automatically.
                    </DialogDescription>
                </DialogHeader>
                {!editing && presetProviderName && (
                    <div
                        data-testid="secondary-provider-note"
                        className="text-xs text-navy-500 bg-cream rounded-lg px-3 py-2 -mt-2"
                    >
                        This dependant will be linked to {presetProviderName}.
                    </div>
                )}
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <FieldRow label="Full name" required>
                            <Input
                                required
                                data-testid="secondary-name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, name: e.target.value }))
                                }
                            />
                        </FieldRow>
                        <FieldRow label="Relationship">
                            <Select
                                value={form.relationship}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, relationship: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="secondary-rel"
                                    className="h-11"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {RELATIONSHIPS.map((r) => (
                                        <SelectItem key={r} value={r}>
                                            {r}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                        <FieldRow label="Date of birth" required>
                            <Input
                                required
                                type="date"
                                data-testid="secondary-dob"
                                value={form.dob}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, dob: e.target.value }))
                                }
                            />
                        </FieldRow>
                        <FieldRow label="Gender">
                            <Select
                                value={form.gender}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, gender: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="secondary-gender"
                                    className="h-11"
                                >
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {GENDERS.map((g) => (
                                        <SelectItem key={g.value} value={g.value}>
                                            {g.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                        <FieldRow label="Email">
                            <Input
                                type="email"
                                data-testid="secondary-email"
                                value={form.email}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, email: e.target.value }))
                                }
                            />
                        </FieldRow>
                        <FieldRow label="Phone">
                            <Input
                                type="tel"
                                data-testid="secondary-phone"
                                value={form.phone}
                                onChange={(e) =>
                                    setForm((f) => ({ ...f, phone: e.target.value }))
                                }
                                placeholder="+852 5555 0000"
                            />
                        </FieldRow>
                        <FieldRow label="Region">
                            <Select
                                value={form.region}
                                onValueChange={(v) =>
                                    setForm((f) => ({ ...f, region: v }))
                                }
                            >
                                <SelectTrigger
                                    data-testid="secondary-region"
                                    className="h-11"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {REGIONS.map((r) => (
                                        <SelectItem key={r.value} value={r.value}>
                                            {r.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FieldRow>
                        <div className="sm:col-span-2">
                            <FieldRow label="Address">
                                <AddressInput
                                    value={form.addr}
                                    onChange={(v) =>
                                        setForm((f) => ({ ...f, addr: v }))
                                    }
                                    testid="secondary-address"
                                />
                            </FieldRow>
                        </div>
                        {!editing && (
                            <FieldRow
                                label="Own login password"
                                hint="Optional. If set (with an email), the secondary member can sign in on their own device using this password."
                                className="sm:col-span-2"
                            >
                                <Input
                                    type="password"
                                    minLength={6}
                                    data-testid="secondary-password"
                                    value={form.password}
                                    onChange={(e) =>
                                        setForm((f) => ({
                                            ...f,
                                            password: e.target.value,
                                        }))
                                    }
                                    placeholder="Minimum 6 characters — leave blank for no login"
                                />
                            </FieldRow>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            className="text-navy-500"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={saving}
                            data-testid="secondary-submit"
                            className="rounded-full bg-navy-700 hover:bg-navy-800 text-white px-6"
                        >
                            <Plus size={14} className="mr-1" />
                            {saving
                                ? "Saving…"
                                : editing
                                  ? "Save changes"
                                  : "Add secondary member"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function FieldRow({ label, required, hint, children, className = "" }) {
    return (
        <div className={className}>
            <Label className="text-navy-700 text-xs uppercase tracking-widest">
                {label}
                {required && <span className="text-gold-600 ml-1">*</span>}
            </Label>
            <div className="mt-1.5">{children}</div>
            {hint && <div className="text-[11px] text-navy-500 mt-1">{hint}</div>}
        </div>
    );
}
